import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import type { GalleryPhoto } from "@tih/shared";
import { PhotoComments } from "./PhotoComments";

type Props = {
  photos: GalleryPhoto[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
};

export function PhotoLightbox({ photos, index, onClose, onNavigate }: Props) {
  const photo = photos[index];
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) onNavigate(index - 1);
  }, [hasPrev, index, onNavigate]);

  const goNext = useCallback(() => {
    if (hasNext) onNavigate(index + 1);
  }, [hasNext, index, onNavigate]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    }
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, goPrev, goNext]);

  if (!photo) return null;

  return createPortal(
    <div
      className="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      onClick={onClose}
    >
      <div className="photo-lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="photo-lightbox-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="photo-lightbox-stage">
          {hasPrev && (
            <button
              type="button"
              className="photo-lightbox-nav photo-lightbox-prev"
              onClick={goPrev}
              aria-label="Previous photo"
            >
              ‹
            </button>
          )}

          <figure className="photo-lightbox-figure">
            <img src={photo.largeUrl ?? photo.url} alt={photo.caption ?? "Gallery photo"} />
            {photo.caption && (
              <figcaption className="photo-lightbox-caption">{photo.caption}</figcaption>
            )}
          </figure>

          {hasNext && (
            <button
              type="button"
              className="photo-lightbox-nav photo-lightbox-next"
              onClick={goNext}
              aria-label="Next photo"
            >
              ›
            </button>
          )}
        </div>

        <aside className="photo-lightbox-sidebar">
          <div className="photo-lightbox-sidebar-head">
            <span className="photo-lightbox-counter">
              {index + 1} of {photos.length}
            </span>
          </div>
          <PhotoComments photoId={photo.id} initialComments={photo.comments} compact />
        </aside>
      </div>
    </div>,
    document.body,
  );
}
